import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus, Minus } from "lucide-react";
import Reveal from "./Reveal";

const metrics = [
  { v: "10.7×", l: "fewer prompt tokens vs. replaying flat conversation history" },
  { v: "87.5%", l: "answer accuracy on the LoCoMo long-memory benchmark" },
  { v: "100%", l: "Hit@10 retrieval on LoCoMo" },
  { v: "181%", l: "3-year ROI from a persistent enterprise memory layer" },
];

const sections = [
  {
    id: "problem",
    tag: "01 · The problem",
    title: "Modern AI is structurally stateless — and your context is fragmented",
    body: [
      "The fundamental limitation of today's AI agents is structural statelessness. Large language models have no native mechanism to persist information across separate API calls, so any context generated in a session lives only inside the active context window. When the session ends or the window fills, the agent suffers immediate context collapse.",
      "For users this means constant repetition, personalization that can't survive long horizons, and agents that forget past workflows and preferences. Developers paper over it by replaying raw, uncompacted transcripts into the prompt — which creates a steep linear cost curve, adds latency, and degrades the model's attention as it hunts for real instructions inside bloated context.",
      "Worse, because people use many independent AI clients — browser copilots, IDE assistants, chat apps — personal context gets trapped inside disconnected, proprietary databases. The result is conflicting histories, agents that can't share reasoning, and hard vendor lock-in: you can't port your interaction history between tools.",
    ],
  },
  {
    id: "concept",
    tag: "02 · The concept",
    title: "UPII: personal context as a systems primitive, not an app feature",
    body: [
      "Unified Personal Intelligence Infrastructure (UPII) promotes personal context from a fragmented app feature to a fundamental systems primitive. “Unified” consolidates raw interaction logs, telemetry and structured knowledge from every digital touchpoint into one standardized schema. “Personal” structures that data around the individual's activities, preferences, workflows and evolving beliefs. “Infrastructure” runs it as a persistent, system-level daemon exposing standard interfaces — such as the Model Context Protocol (MCP) — so any compliant agent can query or update memory dynamically.",
    ],
    bullets: [
      ["Absolute privacy", "A lifelog holds sensitive material — proprietary code, financial priorities. On-device storage removes the risk of cloud leaks and model-retraining exposure."],
      ["Sovereign ownership", "The user holds the primary cryptographic keys, so no cloud provider can change terms, restrict access, or switch off servers."],
      ["Seamless portability", "Decoupling memory from the reasoning engine lets users swap agent frameworks or upgrade foundation models without losing accumulated personalization."],
    ],
  },
  {
    id: "how",
    tag: "03 · How it works",
    title: "The architecture of a local memory substrate",
    body: [
      "A local substrate is not traditional RAG (high-latency vector matching over flat chunks) or fine-tuning (baking data permanently into weights via expensive offline runs). It uses dynamic, multi-axis structures with near-instant asynchronous write-backs.",
    ],
    bullets: [
      ["Capture & ingestion", "Runs asynchronously, outside the agent's execution loop. Multi-step pipelines distill raw logs into structured facts, identify entities, calibrate confidence, and record exact provenance."],
      ["Multi-tier storage", "Organized biologically — short-term working memory, mid-term episodic flows, long-term factual assertions — auto-promoting frequent insights and pruning stale ones."],
      ["Hybrid retrieval", "Semantic graph traversal for logical relationships, plus episodic vector search for sequential similarity, fused at query time."],
      ["Fail-closed security", "Key signatures isolate namespaces and audit every entry; operations default to denied if verification fails."],
      ["Cross-device sync", "Peer-to-peer replication secured by mutual TLS — no cloud broker in the loop."],
    ],
  },
  {
    id: "ueii",
    tag: "04 · UPII vs. UEII",
    title: "Personal sovereignty vs. enterprise collaboration",
    body: [
      "UPII gives individuals a private, sovereign memory layer. The enterprise needs a complementary design — Unified Enterprise Intelligence Infrastructure (UEII) — and the divergence is about governance and the lifecycle of knowledge.",
      "In UPII, rules are self-sovereign: the individual sets data boundaries locally and memory is strictly isolated between tasks. UEII treats intelligence as a company-wide asset on governed lakehouses or hybrid clouds, enforcing Role-Based Access Control and tracing data lineage to prevent internal leaks.",
      "UEII adds cross-functional learning loops: individual task detail stays in short-term history, while verified process improvements are promoted to shared organizational knowledge, so one team's insight can be reused across the whole company. Smritium is built to serve both substrates from one engine.",
    ],
  },
  {
    id: "evidence",
    tag: "05 · The evidence",
    title: "The empirical case for local-first memory",
    body: [
      "Structured agent memory beats raw conversation logging on cost, latency and accuracy. In an 80-turn evaluation, a structured database memory layer held input prompts to ~1,300 tokens while a flat-history baseline grew past 13,900 — up to a 10.7× reduction. Compact serialization cut local payloads by up to 79% versus raw JSON.",
      "Focusing the model on relevant context also improved quality: in the same 80-turn scripted test, structured memory beat the full-transcript baseline 48 turns to 13 (with 19 ties) — despite the baseline seeing the entire verbatim transcript. On the LoCoMo benchmark, lightweight local systems reached 87.5% answer accuracy and a 100% Hit@10 retrieval rate.",
      "At enterprise scale, a business-value study of shared persistent-context infrastructure reported a 181% three-year ROI and a $15.62M net economic benefit, driven by reduced downtime and database-engineering load.",
    ],
  },
  {
    id: "rebuttals",
    tag: "06 · Objections, answered",
    title: "Why local-first holds up",
    body: [],
    bullets: [
      ["“The cloud is simpler.”", "Modern local-first layers run efficiently on consumer hardware — embedded databases like SQLite or DuckDB index years of logs in under five minutes, at a ~30MB footprint with negligible CPU, and skip network latency and API cost entirely."],
      ["“A stolen device is a breach.”", "Local-first avoids the far larger risk of mass cloud breaches. Pairing OS-level encryption with signing every interaction using Ed25519 keypairs makes the store highly resilient to offline tampering."],
      ["“Phones can't run semantic search.”", "Hybrid retrieval mixes fast lexical search with lightweight local embedders (e.g. MiniLM at ~256MB RAM) and structured local knowledge graphs — precise on-device reasoning without cloud-scale vector indexes."],
    ],
  },
  {
    id: "open",
    tag: "07 · Open questions",
    title: "What we're still researching",
    body: ["We publish our blind spots too. Three open problems actively shape the roadmap:"],
    bullets: [
      ["Inter-agent schema standards", "How should different proprietary agents agree on representing entities and temporal relations so memory is truly portable?"],
      ["Energy & thermal cost on-device", "What is the battery and thermal impact of continuous LLM-driven reflection and memory-compaction loops on mobile hardware?"],
      ["Sovereign identity", "How can decentralized peer-to-peer nodes verify user identity without relying on centralized state registries?"],
    ],
  },
];

function Item({ s, open, onToggle }) {
  return (
    <div className="border-b border-white/10">
      <button
        onClick={onToggle}
        className="flex w-full items-start justify-between gap-4 py-6 text-left"
        data-testid={`research-item-${s.id}`}
      >
        <div>
          <p className="font-mono text-[11px] uppercase tracking-overline text-ember">
            {s.tag}
          </p>
          <h3 className="mt-2 font-display text-xl leading-snug text-paper md:text-2xl">
            {s.title}
          </h3>
        </div>
        <span className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/15 text-mute transition-colors group-hover:border-ember">
          {open ? <Minus size={16} /> : <Plus size={16} />}
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="pb-8">
              {s.body.map((p, idx) => (
                <p key={idx} className="mb-4 max-w-3xl leading-relaxed text-mute">
                  {p}
                </p>
              ))}
              {s.bullets && (
                <div className="mt-2 grid gap-3 sm:grid-cols-2">
                  {s.bullets.map(([t, d]) => (
                    <div
                      key={t}
                      className="rounded-xl border border-white/10 bg-void/60 p-4"
                    >
                      <p className="font-display text-paper">{t}</p>
                      <p className="mt-1 text-sm leading-relaxed text-mute">{d}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function ResearchDeepDive() {
  const [openId, setOpenId] = useState("problem");

  return (
    <section
      id="research"
      className="scroll-mt-32 border-t border-white/5 py-24 md:py-28"
      data-testid="research-deep-dive"
    >
      <div className="container-x">
        <Reveal>
          <p className="overline">The research, in depth</p>
          <h2 className="mt-4 max-w-3xl font-display text-3xl font-light leading-tight tracking-tight text-paper md:text-[2.6rem]">
            From stateless agents to a sovereign memory substrate.
          </h2>
          <p className="mt-5 max-w-2xl text-lg font-light leading-relaxed text-mute">
            The complete argument — the problem with stateless AI, the UPII
            concept, the local-first architecture, and the empirical evidence
            behind it.
          </p>
        </Reveal>

        <Reveal delay={0.08}>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 md:grid-cols-4">
            {metrics.map((m) => (
              <div key={m.l} className="card h-full">
                <p className="font-display text-4xl text-ember">{m.v}</p>
                <p className="mt-2 text-sm leading-relaxed text-mute">{m.l}</p>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <div className="mt-14 rounded-2xl border border-white/10 bg-void/40 px-6 md:px-10">
            {sections.map((s) => (
              <div key={s.id} className="group">
                <Item
                  s={s}
                  open={openId === s.id}
                  onToggle={() => setOpenId(openId === s.id ? null : s.id)}
                />
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
